
import React, { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, Minus, Trash2, ArrowRight, Calendar, Clock, Percent, Check } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";

interface OrderCartProps {
  cartCount: number;
  setCartCount: (count: number) => void;
}

interface CartItem {
  id: number;
  name: string;
  image: string;
  sku: string;
  unitPrice: number;
  quantity: number;
  packSize: string;
  sellerName: string; // Added seller name
  priceTier: "Normal" | "Promo" | "Subsidy";
  notes: string;
}

const timeSlots = [
  "9:00 AM - 11:00 AM",
  "11:00 AM - 1:00 PM",
  "1:00 PM - 3:00 PM",
  "3:00 PM - 5:00 PM",
  "5:00 PM - 7:00 PM"
];

// Credit information
const creditInfo = {
  sellerCredit: 2500.00,
  angkasaCredit: 3500.00
};

const OrderCart: React.FC<OrderCartProps> = ({ cartCount, setCartCount }) => {
  const { toast } = useToast();
  
  const [cartItems, setCartItems] = useState<CartItem[]>([
    {
      id: 1,
      name: "Fresh Milk",
      image: "/placeholder.svg",
      sku: "MILK-001",
      unitPrice: 6.90,
      quantity: 10,
      packSize: "1L",
      sellerName: "Farm Fresh Distributor", // Added seller name
      priceTier: "Normal",
      notes: ""
    },
    {
      id: 2,
      name: "White Bread",
      image: "/placeholder.svg",
      sku: "BREAD-001",
      unitPrice: 3.50,
      quantity: 15,
      packSize: "400g",
      sellerName: "Gardenia Foods", // Added seller name
      priceTier: "Normal",
      notes: ""
    },
    {
      id: 4,
      name: "Milo",
      image: "/placeholder.svg",
      sku: "BEV-001",
      unitPrice: 22.90, // Discounted from 23.90
      quantity: 5,
      packSize: "1kg",
      sellerName: "Nestlé Malaysia", // Added seller name
      priceTier: "Promo",
      notes: ""
    },
  ]);

  const [deliveryDate, setDeliveryDate] = useState<Date | undefined>(undefined);
  const [timeSlot, setTimeSlot] = useState<string>(timeSlots[0]);
  const [paymentMethod, setPaymentMethod] = useState("Seller Credit");
  const [voucherCode, setVoucherCode] = useState("");
  const [isSubsidyApplied, setIsSubsidyApplied] = useState(false);
  const [notes, setNotes] = useState("");

  const handleQuantityChange = (id: number, increment: boolean) => {
    setCartItems(items => 
      items.map(item => 
        item.id === id 
          ? { ...item, quantity: increment ? item.quantity + 1 : Math.max(1, item.quantity - 1) }
          : item
      )
    );
    setCartCount(increment ? cartCount + 1 : cartCount - 1);
  };

  const handleRemoveItem = (id: number) => {
    const itemToRemove = cartItems.find(item => item.id === id);
    if (itemToRemove) {
      setCartCount(cartCount - itemToRemove.quantity);
      setCartItems(items => items.filter(item => item.id !== id));
      toast({
        description: `${itemToRemove.name} removed from cart.`,
      });
    }
  };

  const handleItemNoteChange = (id: number, note: string) => {
    setCartItems(items => 
      items.map(item => 
        item.id === id ? { ...item, notes: note } : item
      )
    );
  };

  const handleApplyVoucher = () => {
    if (voucherCode.trim() === "") {
      toast({
        description: "Please enter a voucher code.",
        variant: "destructive"
      });
      return;
    }
    
    if (voucherCode.toUpperCase() === "SAVE10") {
      toast({
        description: "Voucher SAVE10 applied successfully!",
      });
    } else {
      toast({
        description: "Invalid voucher code.",
        variant: "destructive"
      });
    }
  };

  const handleApplySubsidy = () => {
    setIsSubsidyApplied(!isSubsidyApplied);
    toast({
      description: isSubsidyApplied 
        ? "Subsidy removed." 
        : "Subsidy applied successfully!",
    });
  };

  const handleCheckout = () => {
    if (!deliveryDate) {
      toast({
        description: "Please select a delivery date.",
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Order Placed Successfully!",
      description: "Your order has been placed and is being processed.",
    });
    setCartItems([]);
    setCartCount(0);
  };

  const subtotal = cartItems.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);
  const tax = subtotal * 0.06; // 6% tax
  const deliveryFee = 10.00;
  const discount = voucherCode.toUpperCase() === "SAVE10" ? subtotal * 0.1 : 0; // 10% discount
  const subsidyAmount = isSubsidyApplied ? 25.00 : 0;
  const total = subtotal + tax + deliveryFee - discount - subsidyAmount;

  // Calculate remaining credit based on payment method
  const remainingCredit = paymentMethod === "Seller Credit" 
    ? creditInfo.sellerCredit - total
    : paymentMethod === "ANGKASA Credit"
    ? creditInfo.angkasaCredit - total
    : 0;

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium">Order Cart</h3>
      
      {cartItems.length === 0 ? (
        <div className="text-center py-10">
          <p className="text-muted-foreground">Your cart is empty</p>
          <Button className="mt-4 bg-green-600 hover:bg-green-700">
            Continue Shopping
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <Card className="border-green-100">
              <CardContent className="p-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-green-50">
                        <TableHead>Product</TableHead>
                        <TableHead>Seller's Name</TableHead> {/* Added Seller's Name */}
                        <TableHead>Pack Size</TableHead>
                        <TableHead>Unit Price (RM)</TableHead>
                        <TableHead>Quantity</TableHead>
                        <TableHead>Subtotal (RM)</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cartItems.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <img 
                                src={item.image} 
                                alt={item.name} 
                                className="w-10 h-10 object-cover rounded-md"
                              />
                              <div>
                                <p className="font-medium">{item.name}</p>
                                <p className="text-xs text-muted-foreground">SKU: {item.sku}</p>
                                {item.priceTier === "Promo" && (
                                  <span className="text-xs text-amber-600">Promo Price</span>
                                )}
                                {item.priceTier === "Subsidy" && (
                                  <span className="text-xs text-blue-600">Subsidy Item</span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{item.sellerName}</TableCell> {/* Added Seller's Name */}
                          <TableCell>{item.packSize}</TableCell>
                          <TableCell>{item.unitPrice.toFixed(2)}</TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button 
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleQuantityChange(item.id, false)}
                              >
                                <Minus className="h-4 w-4" />
                              </Button>
                              <span className="w-8 text-center">{item.quantity}</span>
                              <Button 
                                variant="outline"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => handleQuantityChange(item.id, true)}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">
                            {(item.unitPrice * item.quantity).toFixed(2)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8 text-red-600"
                              onClick={() => handleRemoveItem(item.id)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
            
            {/* Product Notes */}
            <Card className="border-green-100 mt-4">
              <CardContent className="p-4">
                <h4 className="font-medium mb-4">Product Notes</h4>
                <div className="space-y-4">
                  {cartItems.map((item) => (
                    <div key={`note-${item.id}`} className="space-y-2">
                      <Label htmlFor={`note-${item.id}`}>{item.name}</Label>
                      <Textarea
                        id={`note-${item.id}`}
                        placeholder={`Add notes for ${item.name}...`}
                        value={item.notes}
                        onChange={(e) => handleItemNoteChange(item.id, e.target.value)}
                        className="resize-none"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            {/* Delivery Preferences */}
            <Card className="border-green-100 mt-4">
              <CardContent className="p-4 space-y-4">
                <h4 className="font-medium mb-2">Delivery Preferences</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Delivery Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant={"outline"}
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !deliveryDate && "text-muted-foreground"
                          )}
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {deliveryDate ? format(deliveryDate, "PPP") : <span>Pick a date</span>}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={deliveryDate}
                          onSelect={setDeliveryDate}
                          disabled={(date) => date < new Date()}
                          initialFocus
                          className={cn("p-3 pointer-events-auto")}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Time Slot</Label>
                    <Select value={timeSlot} onValueChange={setTimeSlot}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {timeSlots.map((slot) => (
                          <SelectItem key={slot} value={slot}>
                            <div className="flex items-center">
                              <Clock className="mr-2 h-4 w-4" />
                              {slot}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Order Notes */}
            <Card className="border-green-100 mt-4">
              <CardContent className="p-4">
                <h4 className="font-medium mb-4">Order Notes</h4>
                <Textarea
                  placeholder="Add any special instructions or notes about your order here..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="resize-none"
                />
              </CardContent>
            </Card>
          </div>
          
          <div>
            <Card className="border-green-100">
              <CardContent className="p-4">
                <h4 className="font-medium mb-4">Order Summary</h4>
                
                <div className="space-y-6">
                  {/* Available Credit Information */}
                  <div className="bg-blue-50 p-3 rounded text-sm mb-4">
                    <h5 className="font-semibold mb-2">Available Credit</h5>
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span>Seller Credit:</span>
                        <span className="font-medium">RM {creditInfo.sellerCredit.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>ANGKASA Credit:</span>
                        <span className="font-medium">RM {creditInfo.angkasaCredit.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Payment Method */}
                  <div className="space-y-2">
                    <Label>Payment Method</Label>
                    <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                      <div className="flex items-center space-x-2 rounded-md border p-3">
                        <RadioGroupItem value="Online Payment" id="online" />
                        <Label htmlFor="online" className="flex-1">Online Payment</Label>
                      </div>
                      <div className="flex items-center space-x-2 rounded-md border p-3">
                        <RadioGroupItem value="Seller Credit" id="seller-credit" />
                        <Label htmlFor="seller-credit" className="flex-1">
                          Seller Credit (RM {creditInfo.sellerCredit.toFixed(2)})
                        </Label>
                      </div>
                      <div className="flex items-center space-x-2 rounded-md border p-3">
                        <RadioGroupItem value="ANGKASA Credit" id="angkasa-credit" />
                        <Label htmlFor="angkasa-credit" className="flex-1">
                          ANGKASA Credit (RM {creditInfo.angkasaCredit.toFixed(2)})
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>
                  
                  {/* Voucher & Subsidy */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-2">
                      <Input
                        placeholder="Enter voucher code"
                        value={voucherCode}
                        onChange={(e) => setVoucherCode(e.target.value)}
                      />
                      <Button onClick={handleApplyVoucher} variant="outline">
                        <Percent className="mr-1 h-4 w-4" />
                        Apply
                      </Button>
                    </div>
                    
                    <Button 
                      onClick={handleApplySubsidy} 
                      variant={isSubsidyApplied ? "default" : "outline"}
                      className={isSubsidyApplied ? "bg-blue-600 hover:bg-blue-700 w-full" : "w-full"}
                    >
                      {isSubsidyApplied ? (
                        <>
                          <Check className="mr-1 h-4 w-4" /> Subsidy Applied (RM25.00)
                        </>
                      ) : (
                        "Apply Subsidy"
                      )}
                    </Button>
                  </div>
                  
                  {/* Price Breakdown */}
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Subtotal</span>
                      <span>RM {subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tax (6%)</span>
                      <span>RM {tax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Delivery Fee</span>
                      <span>RM {deliveryFee.toFixed(2)}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-green-600">
                        <span>Discount (Voucher SAVE10)</span>
                        <span>- RM {discount.toFixed(2)}</span>
                      </div>
                    )}
                    {isSubsidyApplied && (
                      <div className="flex justify-between text-blue-600">
                        <span>Subsidy</span>
                        <span>- RM {subsidyAmount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t pt-2">
                    <div className="flex justify-between font-bold">
                      <span>Total</span>
                      <span>RM {total.toFixed(2)}</span>
                    </div>
                  </div>
                  
                  {(paymentMethod === "Seller Credit" || paymentMethod === "ANGKASA Credit") && (
                    <div className={`${remainingCredit >= 0 ? 'bg-green-50' : 'bg-red-50'} p-3 rounded text-sm`}>
                      <p><strong>{paymentMethod === "Seller Credit" ? "Seller Credit" : "ANGKASA Credit"}</strong></p>
                      <p><strong>Available:</strong> RM {(paymentMethod === "Seller Credit" ? creditInfo.sellerCredit : creditInfo.angkasaCredit).toFixed(2)}</p>
                      <p><strong>Remaining After Order:</strong> RM {remainingCredit.toFixed(2)}</p>
                      {remainingCredit < 0 && (
                        <p className="text-red-600 font-semibold mt-1">Insufficient credit. Please select another payment method.</p>
                      )}
                    </div>
                  )}
                  
                  <Button 
                    className="w-full bg-green-600 hover:bg-green-700 mt-4"
                    onClick={handleCheckout}
                    disabled={paymentMethod !== "Online Payment" && remainingCredit < 0}
                  >
                    <Check className="mr-2 h-4 w-4" /> Confirm Order
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderCart;
